import { keepSessionAlive } from 'cadenza/session-polling/session-poller';
import type { RootStateOr } from 'cadenza/store/cadenza-store';
import { getStore } from 'cadenza/store/cadenza-store';
import { assertNonNullable } from 'cadenza/utils/custom-error';
import type { ViewId } from 'cadenza/workbook/api/view-id';
import type { WorksheetDto } from 'cadenza/workbook/api/worksheet-dto';
import type { DataViewId } from 'cadenza/workbook/model/data-view-id';
import { WorksheetSettingsKey } from 'cadenza/workbook/model/worksheet-settings-key';
import type { QueryExecutionStrategyType } from 'cadenza/workbook/query-execution-strategy-type';
import { getSetting } from 'cadenza/workbook/workbook-settings-util';
import type { WorkbookState } from 'cadenza/workbook/workbook.state';
import {
  getActiveWorksheet,
  getActiveWorksheetViewIds,
  getDataViewsByViewId,
  isDataViewOnDemand,
  refreshViewData,
  slice,
  startLiveMode,
  stopLiveMode,
} from 'cadenza/workbook/workbook.state';

import type { Draft, PayloadAction, SliceCaseReducers } from '@reduxjs/toolkit';

interface LiveModeState {
  /** Possible transitions */
  state: 'running' | 'stopped';
  silent?: boolean;
}

interface ViewDataRefreshEvent {
  viewId: ViewId;
  dataViewIds: DataViewId[];
}

export interface WorkbookLiveModeState {
  /** Last live mode status change */
  liveModeState: LiveModeState;
  /**
   * Propagate live mode events via observers to this data structure.
   * The structure is updated periodically by the live mode timer.
   */
  liveModeRefreshEvents: ViewDataRefreshEvent[];
  /** Milliseconds since Epoch when last refresh happened */
  liveModeLastRefreshTime?: number;
}

export function createInitialLiveModeState(): WorkbookLiveModeState {
  return {
    liveModeState: {
      state: 'stopped',
      silent: true,
    },
    liveModeRefreshEvents: [],
  };
}

/* Reducers ---------------------------------------------------------------- */
export const liveModeReducers = {
  refreshViewData(state, { payload: { events } }: PayloadAction<{ events: ViewDataRefreshEvent[] }>) {
    state.liveModeLastRefreshTime = Date.now();
    state.liveModeRefreshEvents = events;
  },
  startLiveMode(state) {
    if (isLiveModeEnabled(state)) {
      state.liveModeState = {
        state: 'running',
      };
      state.liveModeLastRefreshTime = Date.now();
    }
  },
  stopLiveMode(state, { payload: silent = true }: PayloadAction<boolean>) {
    state.liveModeState = {
      state: 'stopped',
      silent,
    };
    state.liveModeLastRefreshTime = undefined;
  },
} satisfies SliceCaseReducers<WorkbookState>;

/* Helper Reducers --------------------------------------------------------- */
/**
 * Use this function to stop the live mode by a non-user (source: 'other') action.
 *
 * @param state - The draft of the WorkbookState
 */
export const stopLiveModeByOther = (state: Draft<WorkbookState>) => {
  state.liveModeState = { state: 'stopped', silent: false };
};

/* Action Creators --------------------------------------------------------- */
export { startLiveMode };

/* Selectors --------------------------------------------------------------- */
function getLiveModeRefreshInterval(worksheet: WorksheetDto) {
  const key = WorksheetSettingsKey.toPersistenceKey('PERIODICAL_QUERY_EXECUTION_INTERVAL');
  const refreshInterval = getSetting<number>(worksheet, key);
  assertNonNullable(refreshInterval);
  return refreshInterval;
}

/**
 * Indicates if the live mode is currently running.
 * @param state
 */
export const isLiveModeRunning = (state: RootStateOr<WorkbookState>) => {
  return slice(state).liveModeState.state === 'running';
};

/**
 * Last change in live mode status with some meta information.
 * @param state
 */
export const getLiveModeState = (state: RootStateOr<WorkbookState>) => {
  return slice(state).liveModeState;
};

export const showLoadingIndicatorInLiveMode = (state: RootStateOr<WorkbookState>) => {
  const worksheet = getActiveWorksheet(state);
  const key = WorksheetSettingsKey.toPersistenceKey('SHOW_LOADING_INDICATORS_IN_LIVE_MODE');
  return getSetting(worksheet, key, true);
};

export const isLiveModeEnabled = (state: RootStateOr<WorkbookState>) => {
  const worksheet = getActiveWorksheet(state);
  const key = WorksheetSettingsKey.toPersistenceKey('QUERY_EXECUTION_STRATEGY');
  return getSetting<QueryExecutionStrategyType>(worksheet, key) === 'periodical';
};

/**
 * Last time a live mode refresh was initiated.
 * @param state
 */
export const getLiveModeLastRefreshTime = (state: RootStateOr<WorkbookState>) => {
  return slice(state).liveModeLastRefreshTime;
};

/**
 * IDs of data views that need a (live mode) refresh for the given viewId.
 *
 * @param state
 * @param viewId
 */
export const getLiveModeDataViewsToRefreshForViewId = (state: RootStateOr<WorkbookState>, viewId: ViewId) => {
  return slice(state).liveModeRefreshEvents.find((event) => event.viewId === viewId)?.dataViewIds;
};

export function toggleLiveMode() {
  if (isLiveModeRunning(getStore().getState())) {
    getStore().dispatch(stopLiveMode(true));
  } else {
    getStore().dispatch(startLiveMode());
  }
}

function handleLiveModeRefresh() {
  const state = slice(getStore().getState());
  const viewDataRefreshEvents: ViewDataRefreshEvent[] = getActiveWorksheetViewIds(state)
    .map((viewId: ViewId) => {
      return {
        viewId,
        dataViewIds: getDataViewsByViewId(state, viewId)
          .filter((dataView) => !isDataViewOnDemand(dataView))
          .map((dataView) => dataView.id),
      };
    })
    .filter(({ dataViewIds }) => dataViewIds.length > 0);
  getStore().dispatch(refreshViewData({ events: viewDataRefreshEvents }));
}

let liveModeRefreshTimer: number | undefined;
let sessionKeepAliveTimer: number | undefined;

export function onLiveModeStateChange(liveModeState: LiveModeState) {
  const isRunning = liveModeState?.state === 'running';
  if (isRunning) {
    const worksheet = getActiveWorksheet(getStore().getState());
    const interval = getLiveModeRefreshInterval(worksheet);

    // Live mode timer
    liveModeRefreshTimer = window.setInterval(() => {
      handleLiveModeRefresh();
    }, interval);

    /**
     * When live mode is running, we keep the session alive in cases where the live mode period
     * refresh time exceeds the session timeout
     */
    sessionKeepAliveTimer = window.setInterval(() => {
      keepSessionAlive();
    }, window.Disy.cadenzaSessionPollingInterval);
  } else {
    window.clearInterval(liveModeRefreshTimer);
    window.clearInterval(sessionKeepAliveTimer);
  }
}
